import React, { memo } from "react";
import { IPhotoLibraryPhoto } from "../../../../interfaces/photoLibrary";
import PhotoLibraryPhotoItem from "./PhotoLibraryPhotoItem";
import "./PhotoLibraryGrid.scss";
import { Skeleton } from "@mui/material";
import IsMobile from "../../../../hooks/isMobile";

interface PhotoLibraryGridProps {
	isLoading: boolean;
	photos: IPhotoLibraryPhoto[];
	selectedPhotos: IPhotoLibraryPhoto[];
	hoveredPhotoId: string | null;
	draggedPhotoId: string | null;
	dragOverPhotoId: string | null;
	deleteConfirmPhotoId: string | null;
	unlinkConfirmPhotoId: string | null;
	onPhotoClick: (photo: IPhotoLibraryPhoto) => void;
	onPhotoHoverStart: (photo: IPhotoLibraryPhoto) => void;
	onPhotoHoverEnd: () => void;
	onPhotoDragStart: (photo: IPhotoLibraryPhoto, e: React.DragEvent) => void;
	onPhotoDragEnd: () => void;
	onPhotoDragOver: (targetPhotoId: string, e: React.DragEvent) => void;
	onPhotoDrop: (targetPhotoId: string, e: React.DragEvent) => void;
	onPreviewPhoto: (photo: IPhotoLibraryPhoto) => void;
	onUnlinkPhoto: (photoId: string, e: React.MouseEvent) => void;
	onDeletePhoto: (photoId: string, e: React.MouseEvent) => void;
	onConfirmDelete: (photo: IPhotoLibraryPhoto, e: React.MouseEvent) => void;
	onCancelDelete: (e: React.MouseEvent) => void;
	onConfirmUnlink: (photo: IPhotoLibraryPhoto, e: React.MouseEvent) => void;
	onCancelUnlink: (e: React.MouseEvent) => void;
}

const PhotoLibraryGrid: React.FC<PhotoLibraryGridProps> = ({
	isLoading,
	photos,
	selectedPhotos,
	hoveredPhotoId,
	draggedPhotoId,
	dragOverPhotoId,
	deleteConfirmPhotoId,
	unlinkConfirmPhotoId,
	onPhotoClick,
	onPhotoHoverStart,
	onPhotoHoverEnd,
	onPhotoDragStart,
	onPhotoDragEnd,
	onPhotoDragOver,
	onPhotoDrop,
	onPreviewPhoto,
	onUnlinkPhoto,
	onDeletePhoto,
	onConfirmDelete,
	onCancelDelete,
	onConfirmUnlink,
	onCancelUnlink,
}) => {
	const isMobile = IsMobile();
	// Create lookup structures for O(1) access instead of O(n) find operations
	const selectedPhotoIds = new Set(selectedPhotos.map((p) => p.id));
	const selectedPhotoIndexMap = new Map(selectedPhotos.map((p, index) => [p.id, index]));

	if (isLoading) {
		return (
			<div className="picker-grid-loading">
				{Array.from({ length: isMobile ? 4 : 8 }).map((_, index) => (
					<div key={index} className="picker-grid-skeleton-item">
						<Skeleton variant="rectangular" width="100%" height="100%" />
					</div>
				))}
			</div>
		);
	}

	if (!photos || photos?.length === 0) {
		return (
			<div className="picker-grid-empty">
				<p>No photos in this section yet</p>
			</div>
		);
	}

	return (
		<div className="picker-grid">
			{photos.map((photo) => {
				const isSelected = selectedPhotoIds.has(photo.id);
				const selectedIndex = selectedPhotoIndexMap.get(photo.id) ?? -1;
				const isHovered = hoveredPhotoId === photo.id;
				const isDragging = draggedPhotoId === photo.id;
				const hasCategory = !!photo.sectionId; // uncategorized section images cannot be unlinked
				const showDeleteConfirm = deleteConfirmPhotoId === photo.id;
				const showUnlinkConfirm = unlinkConfirmPhotoId === photo.id;
				const isDragOver = dragOverPhotoId === photo.id;

				return (
					<PhotoLibraryPhotoItem
						key={photo.id}
						photo={photo}
						isSelected={isSelected}
						selectedIndex={selectedIndex}
						isHovered={isHovered}
						isDragging={isDragging}
						isDragOver={isDragOver}
						hasCategory={hasCategory}
						showDeleteConfirm={showDeleteConfirm}
						showUnlinkConfirm={showUnlinkConfirm}
						onPhotoClick={() => onPhotoClick(photo)}
						onPhotoHoverStart={() => onPhotoHoverStart(photo)}
						onPhotoHoverEnd={onPhotoHoverEnd}
						onPhotoDragStart={(e) => onPhotoDragStart(photo, e)}
						onPhotoDragEnd={onPhotoDragEnd}
						onPhotoDragOver={(e) => onPhotoDragOver(photo.id, e)}
						onPhotoDrop={(e) => onPhotoDrop(photo.id, e)}
						onPreviewClick={(e) => {
							e.stopPropagation();
							onPreviewPhoto(photo);
						}}
						onUnlinkClick={(e) => onUnlinkPhoto(photo.id, e)}
						onDeleteClick={(e) => onDeletePhoto(photo.id, e)}
						onConfirmDelete={(e) => onConfirmDelete(photo, e)}
						onCancelDelete={onCancelDelete}
						onConfirmUnlink={(e) => onConfirmUnlink(photo, e)}
						onCancelUnlink={onCancelUnlink}
					/>
				);
			})}
		</div>
	);
};

export default memo(PhotoLibraryGrid);
